import { sqliteTable, text, integer, index, uniqueIndex } from "drizzle-orm/sqlite-core";
import { sql } from "drizzle-orm";

// ============================================================================
// distribution_models — Distribution Rule headers (EPIC #412 Phase 4A)
//
// A distribution model captures *one* analytic-allocation rule that fires
// automatically at JE-submit time when a JE item has no manually-entered
// allocations. Lines (`accounting_distribution_model_lines`) describe how
// the rule splits the JE item across analytic dimensions.
//
// Trigger conditions (all optional, but at least one MUST be set):
//   * `triggerAccountId`        — exact match on the JE item's account.
//   * `triggerAccountPrefix`    — match if `account.accountNumber` starts
//                                 with this string (e.g. `"5210"` matches
//                                 `5210xx`).
//   * `triggerPartnerId`        — exact match on `jei.partyId`.
//   * `triggerPartnerType`      — `"customer" | "supplier" | NULL`.
//
// Resolver scoring (highest score wins; ties broken by `priority` asc,
// `createdAt` asc): see `services/distribution-rule.service.ts`.
//
// `priority` lets ops override the natural specificity ordering when two
// rules score the same — lower `priority` value wins.
//
// Soft-delete via `isActive=0` per the EPIC spec — fully removing rule
// history is not desired since posted JEs may have been allocated under
// a rule the user later wanted to disable.
// ============================================================================

export const distributionModels = sqliteTable("accounting_distribution_models", {
  id: text("id").primaryKey(), // ULID
  companyId: text("company_id").notNull(),
  code: text("code").notNull(),
  name: text("name").notNull(),
  isActive: integer("is_active").notNull().default(1),

  // Trigger conditions (any combination — at least one required, see route).
  triggerAccountId: text("trigger_account_id"),
  triggerAccountPrefix: text("trigger_account_prefix"),
  triggerPartnerId: text("trigger_partner_id"),
  triggerPartnerType: text("trigger_partner_type"), // "customer" | "supplier"

  // Lower wins on score-tie (default 100 leaves room on both sides for
  // explicit overrides without renumbering existing rules).
  priority: integer("priority").notNull().default(100),

  description: text("description"),

  createdAt: text("created_at").notNull(),
  updatedAt: text("updated_at").notNull(),
  createdBy: text("created_by"),
  updatedBy: text("updated_by"),
}, (table) => ({
  // Partial unique (company, code) — only enforced on active rules so a
  // soft-deleted (isActive=0) rule's code can be reused without forcing
  // the user to rename the deleted rule. Round 1 review (H1).
  distModelCompanyCodeIdx: uniqueIndex("dist_model_company_code_idx")
    .on(table.companyId, table.code)
    .where(sql`is_active = 1`),
  // Resolver hot path: scan only active rules per company.
  distModelCompanyActiveIdx: index("dist_model_company_active_idx")
    .on(table.companyId, table.isActive),
  // Speeds account-prefix lookups when there are many rules.
  distModelAccountPrefixIdx: index("dist_model_account_prefix_idx")
    .on(table.companyId, table.triggerAccountPrefix),
}));

export type DistributionModel = typeof distributionModels.$inferSelect;
export type NewDistributionModel = typeof distributionModels.$inferInsert;
